import {
  Button,
  DateInput,
  Modal,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { createTemporaryUnblock } from '../api';

const keyPrefix = 'financialPortal.currentAccount.financialStatus';

interface FormValues {
  from: string;
  until: string;
}

interface Props {
  isOpen: boolean;
  studentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TemporaryUnblockModal({
  isOpen,
  studentId,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      from: '',
      until: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ from: '', until: '' });
    }
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const submitUnblock = useCallback(
    async (values: FormValues) => {
      setLoading(true);
      const result = await createTemporaryUnblock(studentId, {
        from: values.from,
        until: values.until,
      });
      setLoading(false);

      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${keyPrefix}.errorCreating`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${keyPrefix}.successCreating`),
      });
      handleClose();
      onSuccess();
    },
    [studentId, t, handleClose, onSuccess],
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${keyPrefix}.confirmTitle`),
        subtitle: t(`${keyPrefix}.confirmSubtitle`),
        btnConfirm: {
          text: t(`${keyPrefix}.confirm`),
          onConfirm: () => submitUnblock(values),
        },
        btnClose: { text: t(`${keyPrefix}.cancel`) },
      });
    },
    [t, submitUnblock],
  );

  const requiredRule = { required: t(`${keyPrefix}.required`) };

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <div className="text-center mb-4">
        <h1 className="text-dark fs-22">{t(`${keyPrefix}.modalTitle`)}</h1>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6} className="pb-3">
            <DateInput
              name="from"
              label={t(`${keyPrefix}.fromLabel`)}
              control={control}
              rules={requiredRule}
            />
          </Col>
          <Col xs={12} md={6} className="pb-3">
            <DateInput
              name="until"
              label={t(`${keyPrefix}.untilLabel`)}
              control={control}
              rules={requiredRule}
            />
          </Col>
        </Row>
        <Row className="pt-4">
          <Col xs={12} sm={6}>
            <Button
              text={t(`${keyPrefix}.cancel`)}
              outlined
              fullwidth
              onClick={handleClose}
              disabled={loading}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Button
              text={t(`${keyPrefix}.save`)}
              fullwidth
              type="submit"
              loading={loading}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
